/**
 * Purchase Bill Model & Calculations
 * Handles Vendor Inward Bills, Goods Received, GST & Non-GST calculations
 */

export const PURCHASE_STATUS = {
  PAID: "Paid",
  UNPAID: "Unpaid",
  PARTIAL: "Partial"
};

export const PURCHASE_TYPES = {
  GST: "GST",       // GST Purchase Bill (CGST + SGST or IGST)
  NORMAL: "Normal"  // Non-GST / Cash Bill (0% Tax)
};

// TCS (Tax Collected at Source) the vendor charges on taxable value + GST. Not GST input credit.
export const TCS_RATES = [0, 0.1, 1, 2, 5];

export function calculatePurchaseLineItem(item, qty = 1, customRate = null, isNonGst = false, qtyBreakdown = null) {
  const quantity = Number(qty || 1);
  const rate = customRate !== null && customRate !== undefined
    ? Number(customRate)
    : Number(item.costPrice || item.basicPrice || item.sellingPrice || 0);

  const gstPercent = isNonGst ? 0 : Number(item.gstPercent || 0);
  const subtotal = Math.round(quantity * rate * 100) / 100;
  const gstAmount = isNonGst ? 0 : Math.round((subtotal * gstPercent) / 100 * 100) / 100;
  const total = Math.round((subtotal + gstAmount) * 100) / 100;

  const breakdown = Array.isArray(qtyBreakdown)
    ? qtyBreakdown
    : Array.isArray(item.qtyBreakdown)
      ? item.qtyBreakdown
      : null;

  return {
    itemId: item.id || "",
    itemName: item.itemName || item.name || "",
    itemType: item.itemType || "goods",
    gstCode: item.gstCode || item.sku || "",
    unit: item.unit || "pcs",
    qty: quantity,
    rate,
    gstPercent,
    lineSubtotal: subtotal,
    lineGst: gstAmount,
    lineTotal: total,
    qtyBreakdown: breakdown
  };
}

/**
 * tcs: { rate, amount } — amount (₹) overrides rate × (taxable + GST) so it can match the vendor's bill exactly.
 */
export function calculatePurchaseTotals(lineItems = [], vendor = null, discountAmount = 0, billType = "GST", company = null, tcs = null) {
  const isNonGst = billType === PURCHASE_TYPES.NORMAL || billType === "Normal";
  const buyerStateCode = company?.stateCode || "33";
  const vendorStateCode = vendor?.stateCode || (vendor?.gstin ? vendor.gstin.substring(0, 2) : buyerStateCode);
  const isInterState = !isNonGst && vendorStateCode && vendorStateCode !== buyerStateCode;

  let subtotal = 0;
  let gstTotal = 0;

  lineItems.forEach((li) => {
    subtotal += Number(li.lineSubtotal || 0);
    if (!isNonGst) {
      gstTotal += Number(li.lineGst || 0);
    }
  });

  const discount = Math.min(Number(discountAmount || 0), subtotal);
  const discountedSubtotal = subtotal - discount;

  let finalGst = 0;
  let cgst = 0;
  let sgst = 0;
  let igst = 0;

  if (!isNonGst) {
    finalGst = gstTotal;
    if (discount > 0 && subtotal > 0) {
      const ratio = discountedSubtotal / subtotal;
      finalGst = Math.round(gstTotal * ratio * 100) / 100;
    }

    if (isInterState) {
      igst = finalGst;
    } else {
      cgst = Math.round((finalGst / 2) * 100) / 100;
      sgst = Math.round((finalGst - cgst) * 100) / 100;
    }
  }

  const tcsRate = Math.max(0, Number(tcs?.rate || 0));
  const tcsManual = tcs?.amount !== null && tcs?.amount !== undefined && tcs?.amount !== "";
  const tcsBase = Math.round((discountedSubtotal + finalGst) * 100) / 100;
  const tcsAmount = tcsManual
    ? Math.round(Math.max(0, Number(tcs.amount) || 0) * 100) / 100
    : Math.round((tcsBase * tcsRate) / 100 * 100) / 100;

  const rawTotal = discountedSubtotal + finalGst + tcsAmount;
  const grandTotal = Math.round(rawTotal);
  const roundOff = Math.round((grandTotal - rawTotal) * 100) / 100;

  return {
    subtotal: Math.round(subtotal * 100) / 100,
    discountAmount: Math.round(discount * 100) / 100,
    taxableAmount: Math.round(discountedSubtotal * 100) / 100,
    gstTotal: finalGst,
    cgst,
    sgst,
    igst,
    isInterState,
    isNonGst,
    tcsRate,
    tcsBase,
    tcsAmount,
    tcsManual,
    roundOff,
    grandTotal,
    balance: grandTotal
  };
}
